import React from "react"
import styled, { css } from "styled-components"
import { Link } from "gatsby-plugin-react-intl"

import { isInternalLink } from "../helpers"
import { media } from "../styles"

export const ButtonStyles = css`
  align-items: center;
  appearance: none;
  border: 1px solid currentColor;
  color: ${({ theme }) => theme.brandPrimaryDark};
  background-color: transparent;
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeightBold};
  letter-spacing: 0.02em;
  line-height: 1;
  padding: 10px 30px;
  text-align: center;
  text-decoration: none;
  transition: ${({ theme }) => theme.transitionTime};
  transition-property: background, border, box-shadow, color, opacity;

  &:hover {
    background-color: ${({ theme }) => theme.brandPrimaryDark};
    border-color: transparent;
    color: ${({ theme }) => theme.brandPrimaryLight};
    text-decoration: none;
  }

  svg {
    color: currentColor;
    margin-left: auto;
  }
`

export const ButtonStylesLarge = css`
  display: block;
  margin: 20px 0 10px;
  padding: 17px 15px;
  text-align: center;
  width: 100%;

  @media ${media.sm} {
    display: inline-block;
    font-size: 16px;
    margin: 0 0 10px;
    padding: 17px 30px;
    min-width: 360px;
    width: auto;
  }
`

export const ButtonStylesPrimary = css`
  background-color: ${({ theme }) => theme.brandPrimaryDark};
  border-color: ${({ theme }) => theme.brandPrimaryDark};
  color: ${({ theme }) => theme.brandPrimaryLight};

  &:hover {
    background-color: ${({ theme }) => theme.brandPrimaryMedium};
    border-color: ${({ theme }) => theme.brandPrimaryMedium};
    color: ${({ theme }) => theme.brandPrimaryDark};
  }
`

const ButtonComponent = styled.div`
  ${ButtonStyles}

  ${(props) =>
    props.as === "button" &&
    css`
      text-align: center;
    `}

  ${(props) =>
    props.kind === "outline" &&
    css`
      border-color: ${({ theme }) => theme.brandPrimaryDark};
      background-color: transparent;
      color: ${({ theme }) => theme.brandPrimaryDark};

      &:hover {
        border-color: ${({ theme }) => theme.brandPrimaryMedium};
        background-color: ${({ theme }) => theme.brandPrimaryMedium};
        color: ${({ theme }) => theme.brandPrimaryDark};
      }
    `}

  ${(props) =>
    props.kind === "primary" &&
    css`
      ${ButtonStylesPrimary}
    `}

  ${(props) =>
    props.kind === "primary-linkhover" &&
    css`
      background-color: ${({ theme }) => theme.brandPrimaryDark};
      border-color: ${({ theme }) => theme.brandPrimaryDark};
      color: ${({ theme }) => theme.brandPrimaryLight};

      &:hover {
        background-color: ${({ theme }) => theme.brandLinksHover};
        border-color: ${({ theme }) => theme.brandLinksHover};
        color: ${({ theme }) => theme.brandPrimaryLight};
      }
    `}

    ${(props) =>
    props.kind === "primary-outline" &&
    css`
      border-color: ${({ theme }) => theme.brandPrimaryDark};
      background-color: transparent;
      color: ${({ theme }) => theme.brandPrimaryDark};

      &:hover {
        border-color: ${({ theme }) => theme.brandPrimaryDark};
        background-color: ${({ theme }) => theme.brandPrimaryDark};
        color: ${({ theme }) => theme.brandPrimaryLight};
      }
    `}

${(props) =>
    props.kind === "secondary" &&
    css`
      background-color: ${({ theme }) => theme.brandPrimaryMedium};
      border-color: ${({ theme }) => theme.brandPrimaryMedium};
      color: ${({ theme }) => theme.brandPrimaryDark};

      &:hover {
        background-color: ${({ theme }) => theme.brandPrimaryDark};
        border-color: ${({ theme }) => theme.brandPrimaryDark};
        color: ${({ theme }) => theme.brandPrimaryMedium};
      }
    `}

${(props) =>
    props.kind === "secondary-outline" &&
    css`
      border-color: ${({ theme }) => theme.brandPrimaryMedium};
      background-color: transparent;
      color: ${({ theme }) => theme.brandPrimaryDark};

      &:hover {
        border-color: ${({ theme }) => theme.brandPrimaryMedium};
        background-color: ${({ theme }) => theme.brandPrimaryMedium};
        color: ${({ theme }) => theme.brandPrimaryDark};
      }
    `}

${(props) =>
    props.kind === "secondary-outline-inverted" &&
    css`
      border-color: ${({ theme }) => theme.brandPrimaryMedium};
      background-color: transparent;
      color: ${({ theme }) => theme.brandPrimaryLight};

      &:hover {
        border-color: ${({ theme }) => theme.brandPrimaryMedium};
        background-color: ${({ theme }) => theme.brandPrimaryMedium};
        color: ${({ theme }) => theme.brandPrimaryDark};
      }
    `}

${(props) =>
    props.kind === "light-outline" &&
    css`
      border-color: ${({ theme }) => theme.brandPrimaryLight};
      background-color: transparent;
      color: ${({ theme }) => theme.brandPrimaryLight};

      &:hover {
        border-color: ${({ theme }) => theme.brandPrimaryMedium};
        background-color: ${({ theme }) => theme.brandPrimaryMedium};
        color: ${({ theme }) => theme.brandPrimaryDark};
      }
    `}

${(props) =>
    props.arrow &&
    css`
      text-align: left;
      padding: 10px;
      width: auto;

      > span {
        margin-right: 10px;
      }
    `}

${(props) =>
    props.size === "large" &&
    css`
      ${ButtonStylesLarge}
    `}
`

const Button = ({
  arrow,
  as,
  children,
  className,
  href,
  onClick,
  kind,
  size,
  target,
  type,
  ...props
}) => {
  const link = {}
  let element = "a"

  if (as) {
    element = as
  } else {
    if (onClick) {
      element = "button"
    } else {
      if (isInternalLink(href)) {
        element = Link
        link.to = href
      } else {
        element = "a"
        link.href = href
      }
    }
  }

  return (
    <ButtonComponent
      arrow={arrow}
      as={element}
      className={`${className} button`}
      kind={kind}
      onClick={onClick ? () => onClick() : null}
      size={size}
      target={target || "_top"}
      type={type}
      {...link}
      {...props}
    >
      <>
        <span>{children}</span>
        {arrow && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="12"
            viewBox="0 0 11 12"
          >
            <g
              fill="none"
              fillRule="evenodd"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M4.3993 1.235l4.8484 4.8273-4.8484 4.8274" />
              <path strokeLinecap="square" d="M1 6.0623h7" />
            </g>
          </svg>
        )}
      </>
    </ButtonComponent>
  )
}

export default Button
